import { useMemo } from "react";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useColumnSortFunc } from "../blocks/ColumnSort/useColumnSortFunc";
import { useColumnSortState } from "../blocks/ColumnSort/useColumnSortState";
import { useOrdersCriteriaParam } from "../blocks/MyQuotes/filters/Criteria/CriteriaInput";
import { useOrdersDateParam } from "../blocks/MyQuotes/filters/Date/OrdersDateSelect";
import { getMinimumDate } from "../blocks/MyQuotes/filters/Date/getMinimumDate";
import { useOrdersSizeLimit } from "../blocks/MyQuotes/filters/Size/useOrdersSizeLimit";
import {
  rimDiameterValue,
  worstTreadInches,
} from "../blocks/MyQuotes/hunterTireFormatting";
import { useDealerAccountInfo } from "../stores/hooks/useDealerAccountInfo";
import { useUrl } from "../stores/hooks/useUrl";

type HunterTireResult = ModifiedSWRResponse<
  FetcherResult<HunterTireResponse>,
  HunterTireItem[]
>;

const dateFields = new Set(["date"]);

const numericFields = new Set(["rimSize", "treadDepth"]);

/**
 * Most records carry neither a rim size nor a tread reading — only 32 of one QA
 * dealer's 2,389 have tread — so blanks are held at the bottom in both
 * directions rather than burying every row that has a value. The O/E size is
 * blank for the same reason whenever the vehicle never matched our taxonomy.
 */
const emptyLastFields = new Set(["oeTireSize", "rimSize", "treadDepth"]);

export const useGetHunterTire = (): HunterTireResult => {
  const { dealerId } = useDealerAccountInfo();
  const { search } = useUrl();
  const size = useOrdersSizeLimit();
  const [dateParam] = useOrdersDateParam();
  const [criteria] = useOrdersCriteriaParam();
  const sort = useColumnSortState("hunterTire");
  const sortFunc = useColumnSortFunc("hunterTire", {
    dateFields,
    emptyLastFields,
    numericFields,
  });

  const { data, ...rest } = useSwrGet<HunterTireResponse>(
    "/retailSiteWebService/parts/tire/hunter.php",
    {
      dealer_id: dealerId,
      per_page: "100",
    },
    {
      revalidateIfStale: true,
    },
  );

  const result = useMemo(() => {
    const minDate = getMinimumDate(dateParam);
    const searchTerm = criteria.trim().toLowerCase();

    return data
      ?.filter((item) => new Date(item.created_at) >= minDate)
      .filter((item) => {
        if (!searchTerm) return true;
        const { vehicle_info } = item;
        const textRow = [
          item.vin,
          item.test_type,
          item.order_number,
          item.tire_size,
          item.oe_tire_size,
          vehicle_info?.manufacturer,
          vehicle_info?.model,
          vehicle_info?.year,
          vehicle_info?.registration,
          vehicle_info?.color,
        ];
        return textRow.join(" ").toLowerCase().includes(searchTerm);
      })
      // Sorting runs on a flat projection whose keys are the sort field names,
      // which is what the shared comparator keys on, then unwraps back to the
      // items the table renders.
      .map((item) => ({
        date: item.created_at,
        item,
        oeTireSize: item.oe_tire_size || "",
        rimSize: rimDiameterValue(item.rim_diameter),
        testType: item.test_type || "",
        treadDepth: worstTreadInches(item.tread_depth),
        vehicle: [
          item.vehicle_info?.year,
          item.vehicle_info?.manufacturer,
          item.vehicle_info?.model,
        ]
          .filter(Boolean)
          .join(" "),
        vin: item.vehicle_info?.vin || item.vin || "",
      }))
      .sort(sortFunc)
      .map((row) => row.item)
      .slice(0, size);
  }, [data, search, dateParam, criteria, size, sortFunc, sort.field, sort.order]);

  return {
    data: result,
    ...rest,
  };
};

type HunterTireResponse = HunterTireItem[];

export type HunterTireItem = {
  created_at: string;
  id: number;
  /**
   * The O/E size our fitment data holds for the vehicle, resolved server-side
   * from year/make/model. Null when the vehicle never matched the taxonomy.
   * Distinct from tire_size, which is what Hunter measured on the car.
   */
  oe_tire_size: null | string;
  order_number: null | string;
  // decimal:2 on the model, so this arrives as a string over JSON.
  rim_diameter: null | number | string;
  rim_diameter_unit: null | string;
  summary: unknown[];
  test_type: string;
  tire_size: null | string;
  tread_depth: HunterTreadDepth | null;
  vehicle_info: HunterVehicleInfo;
  vin: string;
};

/**
 * Per-corner tread depth as extracted at ingest, keyed by corner
 * (front_left, rear_right, and axle3_left style keys on multi-axle vehicles).
 */
export type HunterTreadDepth = Record<string, HunterTreadCorner>;

export type HunterTreadCorner = {
  /** The shallowest of the readings — what a tread report is judged on. */
  min: HunterTreadReading | null;
  readings: Record<string, HunterTreadReading>;
};

export type HunterTreadReading = {
  /** Normalised from whatever unit the equipment reported. */
  inches: null | number;
  position?: null | string;
  /** Hunter's own grade: 1 pass, 2 review, 3 fail. */
  result: null | string;
  unit: null | string;
  value: string;
};

type HunterVehicleInfo = {
  color: string;
  inspection_features: InspectionFeature[];
  is_light_duty: string;
  manufacturer: string;
  model: string;
  number_of_axles: string;
  odometer: number;
  order_date: string;
  order_date_utc: string;
  order_number: null | string;
  registration: string;
  vehicle_description: null | string;
  vehicle_type: null | string;
  vin: string;
  year: string;
};

type InspectionFeature = {
  mid: string;
  name: string;
  value: string;
};
